import SpinningIcon from '@/components/common/SpinningIcon';
import { useApplication } from '@/hooks/useApplication';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { useViewportContext } from '@/hooks/useViewport';
import { ValidationError } from '@/types/validation';
import { findSubroutine } from '@/util/application';
import { setOpacity } from '@/util/color';
import { isNullOrUndefined, isNumber } from '@/util/typeGuards';
import { validateApplication } from '@/util/validation/validation';
import { AccountTree, ExpandLess, ExpandMore, Loop } from '@mui/icons-material';
import { Divider, Icon, Stack, Typography, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import ErrorBadges from './ErrorBadges';
import ErrorLine from './ErrorLine';

const ValidatorBar: FC = () => {
  const theme = useTheme();
  const { application } = useApplication();
  const { selectedSubroutineId, setSelectedSubroutineId } = useSubroutine();
  const { updateNodes, setUpdateNodes } = useUpdateNodes();
  const { metadata } = useMetadata();
  const { errors, setErrors } = useErrorHandler();
  const { setCenterOnNode } = useViewportContext();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = ({ id, subroutineId }: ValidationError) => {
    if (!isNullOrUndefined(id)) {
      setCenterOnNode(id.toString());
    } else if (!isNullOrUndefined(subroutineId)) {
      setSelectedSubroutineId(subroutineId);
    }
  };

  const barHeight = theme.spacing(3);

  useEffect(() => {
    if (!updateNodes.length) return;
    console.debug('validating nodes: ', updateNodes);
    setLoading(true);
    const existingErrors = updateNodes.includes('*') ? [] : errors;
    const validateNodes = updateNodes.includes('*') ? Object.keys(application.blocks.nodes) : updateNodes;
    const newErrors = validateApplication(application, metadata, validateNodes);

    const seen = new Set();

    const filteredErrors = [
      ...existingErrors.filter((error) => isNumber(error.id) && !updateNodes.includes(error.id.toString())),
      ...newErrors
    ]
      .filter((error) => {
        const key = `#${error.id} (${error.severity}) ${error.message}`;
        return !seen.has(key) && seen.add(key);
      })
      .sort((a, b) => {
        if (!isNumber(a.id)) return -1;
        if (!isNumber(b.id)) return 1;
        return a.id - b.id;
      });

    setErrors(filteredErrors);
    setLoading(false);
    setUpdateNodes([]);
  }, [updateNodes]);

  return (
    <Stack
      order={1}
      flexShrink={0}
      bgcolor={theme.palette.background.paper}
      height={open ? theme.spacing(50) : barHeight}
      sx={{ transition: 'height 0.3s ease-in-out' }}
    >
      <Stack minHeight={barHeight}>
        <Divider />
        <Stack
          color={theme.palette.primary.contrastText}
          direction='row'
          justifyContent='end'
          alignItems='space-between'
          height='100%'
          onClick={() => setOpen(!open)}
          sx={{
            transition: 'all 0.1s',
            pointerEvents: 'visible',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: setOpacity(theme.palette.action.hover, 0.05)
            }
          }}
        >
          <Stack
            direction='row'
            mr='auto'
            ml='auto'
            sx={{
              color: theme.palette.text.primary,
              opacity: 0.5,
              transition: 'opacity 0.1s',
              '&:hover': {
                opacity: 1
              }
            }}
          >
            {open ? <ExpandMore /> : <ExpandLess />}
          </Stack>
          {loading && (
            <SpinningIcon
              sx={{ color: theme.palette.text.primary }}
              icon={Loop}
              iconProps={{
                fontSize: 'small'
              }}
            />
          )}
          <Stack
            height='100%'
            direction='row'
            alignItems='center'
            color={theme.palette.common.white}
            minWidth={theme.spacing(6)}
            gap={0.5}
            p={0.25}
            bgcolor='gray'
          >
            <Icon component={AccountTree} />
            <Typography variant='caption'>
              {findSubroutine(application.subroutines, selectedSubroutineId)?.nodes.length.toString()}
            </Typography>
          </Stack>
          <ErrorBadges errors={errors} />
        </Stack>
        {open && <Divider />}
      </Stack>
      <Stack height='100%' sx={{ overFlowX: 'scroll', overflowY: 'scroll' }}>
        {errors.map((problem, index) => (
          <ErrorLine problem={problem} onClick={handleClick} key={`problem-line-${index}`} />
        ))}
      </Stack>
    </Stack>
  );
};

export default ValidatorBar;
